// Module 09 §8 — THE save funnel. Every dashboard write in v4 goes through `execute`.
//
// It replaces five divergent v3 call sites that each built their own body and their own error
// handling:
//   1. `CustomDashboard.vue:234`            update      — sent `version`
//   2. `CustomDashboard.vue:447`            complete    — sent `version`
//   3. `CustomDashboardStarterStep.vue:617` duplicate   — NO `version`
//   4. `AddToDashboardModal.vue:224`        add-to-dash — NO `version`  ← silent clobber
//   5. `AccountCenter.vue:124`              account ctr — different endpoint + `account_center_id`
//
// (3) is a create, so omitting `version` was harmless. (4) was not: it re-sent the WHOLE
// `sections` array of an existing dashboard with no concurrency token, so appending a report
// view overwrote whatever anyone else had saved since the modal opened. Routing it here fixes
// that by construction — `version` is attached from the input, not from the call site's memory.
//
// DELIBERATELY NO TOAST AND NO ROUTER. The flow returns an outcome carrying an i18n key and, when
// the canonical url changed, a route target; the screen renders and navigates. That is the split
// the repo already uses (`useLogin.ts` resolves a failure to a key, the form renders it) and it
// is what makes every branch below assertable without mounting anything. A caller that wants the
// navigation performed for it passes `navigate`.
import type { DashboardApi } from '../api/DashboardApi'
import { dashboardApi } from '../api/DashboardApi'
import type {
  AppendToDashboardInput,
  AppendToDashboardOutcome,
} from '../models/addToDashboard.types'
import type { DashboardDefinitionDto, UpsertBody } from '../models/api.types'
import type { SaveDashboardInput, SaveDashboardOutcome, DashboardScope } from '../models/save.types'
import {
  buildAccountCenterRoute,
  buildCustomDashboardRoute,
  type DashboardRouteTarget,
} from '../service/dashboardRoute'
import { serializeLayout, parseLayout } from '../service/layout/serialize'
import { dashboardUrlFromName } from '../service/snakeCaseUrl'
import { useDashboardStore } from '../stores/dashboardStore'

import { parseSectionsValidationErrors } from './sectionsValidation'

import { ApiError } from '@/shared/http'

/**
 * `Copy of ` is a PERSISTED VALUE, not UI chrome: it becomes the new dashboard's `name` column
 * and its slugified `url`. It stays an English literal for that reason (and because the product
 * is English-only) — localising it would write a locale-dependent identifier to the database.
 * v3 had two spellings for this, `'Copy of ' + name` (`CustomDashboard.vue:439`,
 * `AccountCenter.vue:115`) and `` `Copy ${name}` `` (`CustomDashboardStarterStep.vue:608`);
 * collapsing to one funnel picks the majority spelling.
 */
export const COPY_PREFIX = 'Copy of '

export interface SaveDashboardFlowOptions {
  api?: DashboardApi
  /** Performed when the saved `url_id` differs from the one on screen. Usually `router.replace`. */
  navigate?: (target: DashboardRouteTarget) => void | Promise<void>
  /** Fires on `403` so credits/account can open the limit modal — 09 signals, it does not own it. */
  onLimitReached?: () => void
}

export class SaveDashboardFlow {
  private readonly api: DashboardApi
  private readonly options: SaveDashboardFlowOptions

  constructor(options: SaveDashboardFlowOptions = {}) {
    this.api = options.api ?? dashboardApi
    this.options = options
  }

  async execute(input: SaveDashboardInput): Promise<SaveDashboardOutcome> {
    const trimmed = input.name.trim()
    if (!trimmed) {
      return { status: 'nameRequired', messageKey: 'dashboards.save.nameRequired' }
    }
    const name = input.duplicate ? `${COPY_PREFIX}${trimmed}` : trimmed

    const scope: DashboardScope = input.scope ?? 'workspace'
    // A duplicate is a CREATE from existing rows: no id, and therefore no version.
    const isExisting = !input.duplicate && input.id != null
    const body = this.buildBody({ input, name, isExisting })

    try {
      // The account-center endpoint keys the existing record `account_center_id`; `id` must not
      // ride along beside it, or the backend sees two identity keys for one record.
      const { id, ...common } = body
      const definition =
        scope === 'accountCenter'
          ? await this.api.upsertAccountCenter(input.ownerId, {
              ...common,
              ...(id != null ? { account_center_id: id } : {}),
            })
          : await this.api.upsert(input.ownerId, body)

      return await this.adopt(definition, input, scope, isExisting)
    } catch (cause) {
      return await this.classify(cause, input, scope)
    }
  }

  /**
   * The 08→09 bridge. Resolution stays module-08-owned through the injected verdict; this flow
   * owns partial-add semantics and funnels the surviving widget rows through the versioned save.
   */
  async append(input: AppendToDashboardInput): Promise<AppendToDashboardOutcome> {
    const resolved = input.widgets.filter(input.canResolve)
    const skipped = input.widgets.length - resolved.length
    if (resolved.length === 0) {
      return {
        status: 'unresolvable',
        skipped,
        messageKey: 'dashboards.addTo.unresolvable',
      }
    }

    const stamp = Date.now()
    const appendedSections = resolved.map((widget, index) => {
      const rowId = `add-to-dashboard-${stamp}-${index}`
      return {
        id: rowId,
        name: '',
        cols: [
          {
            id: `${rowId}-col`,
            name: typeof widget.name === 'string' ? widget.name : '',
            width: 100,
            ...widget,
          },
        ],
      }
    })

    const rows = parseLayout([...serializeLayout(input.target.rows), ...appendedSections])
    const outcome = await this.execute({
      ...input.target,
      rows,
      navigateAfterSave: true,
    })
    return { ...outcome, skipped }
  }

  /**
   * The one place a save body is built. `version` rides EVERY existing-dashboard save — that is
   * the add-to-dashboard clobber fix — and `template_id` only rides a create, because a template
   * seeds a new dashboard and re-declaring it on an update tells the backend the record changed
   * provenance (v3 `CustomDashboard.vue:227-229`; `complete:435-437` sent it on updates too,
   * which is the divergence this collapse resolves).
   */
  private buildBody(args: {
    input: SaveDashboardInput
    name: string
    isExisting: boolean
  }): UpsertBody {
    const { input, name, isExisting } = args
    const body: UpsertBody = {
      name,
      url: dashboardUrlFromName(name),
      sections: serializeLayout(input.rows),
    }
    if (isExisting && input.id != null) {
      body.id = input.id
      if (input.version != null) body.version = input.version
    } else if (input.templateId != null) {
      body.template_id = input.templateId
    }
    return body
  }

  private async adopt(
    definition: DashboardDefinitionDto,
    input: SaveDashboardInput,
    scope: DashboardScope,
    isExisting: boolean,
  ): Promise<SaveDashboardOutcome> {
    const store = useDashboardStore()
    if (scope === 'accountCenter') store.upsertAccountCenterLocal(definition)
    else store.upsertLocal(definition)

    // v3 reloaded the whole list after every save AND then called `window.location.reload()`
    // (`CustomDashboard.vue:236`). Neither is needed: the store is reactive and the returned DTO
    // is the record that changed, so the local patch above IS the re-hydrate.
    const urlChanged = Boolean(definition.url_id) && definition.url_id !== input.currentUrlId
    let redirectTo: DashboardRouteTarget | null = null
    if (urlChanged || input.navigateAfterSave === true) {
      // An account center lives on `account-center/:url?`, not on the dashboard record's
      // `:account_id/:workspace_id/:url` — building the dashboard target here would emit params
      // its route never declares.
      redirectTo =
        scope === 'accountCenter'
          ? buildAccountCenterRoute(definition)
          : buildCustomDashboardRoute(
              { url_id: definition.url_id, workspace_id: definition.workspace_id },
              {
                ...(input.accountId === undefined ? {} : { accountId: input.accountId }),
                workspaceId: input.ownerId,
              },
            )
    }

    if (redirectTo) await this.options.navigate?.(redirectTo)

    return {
      status: 'saved',
      definition,
      created: !isExisting,
      duplicated: Boolean(input.duplicate),
      redirectTo,
      messageKey: input.duplicate
        ? 'dashboards.save.duplicated'
        : isExisting
          ? 'dashboards.save.updated'
          : 'dashboards.save.created',
    }
  }

  /**
   * Failure classification by `ApiError.kind` — never by status sniffing (`err?.response?.status
   * === 409` at `CustomDashboard.vue:198`, `=== 403` at `AddToDashboardModal.vue:191`), which is
   * exactly what ledger A2 removes.
   */
  private async classify(
    cause: unknown,
    input: SaveDashboardInput,
    scope: DashboardScope,
  ): Promise<SaveDashboardOutcome> {
    const error = ApiError.isApiError(cause) ? cause : ApiError.fromAxios(cause)

    if (error.kind === 'conflict') {
      return {
        status: 'conflict',
        latest: await this.reconcile(input, scope),
        messageKey: 'dashboards.save.conflict',
      }
    }

    if (error.kind === 'forbidden') {
      this.options.onLimitReached?.()
      return { status: 'limitReached', messageKey: 'dashboards.save.limitReached' }
    }

    // 400 = the backend read INSIDE `sections` and refused it. 422 is the same family of verdict
    // and both arrive as `kind:'validation'`; only a payload that actually names widgets is
    // reported as such, so a plain 400 with no detail still falls through to `failed`.
    if (error.kind === 'validation') {
      const widgets = parseSectionsValidationErrors(error.payload)
      if (widgets.length > 0 || error.status === 400) {
        return { status: 'invalidSections', widgets, messageKey: 'dashboards.save.invalidSections' }
      }
    }

    return { status: 'failed', error, messageKey: 'dashboards.save.failed' }
  }

  /**
   * The 409 path. Reload the latest record and hand it back — the caller swaps its working copy
   * and reapplies. The rejected payload is never re-sent, which is what "no clobber" means here:
   * the loser of the race loses their unsaved edits from the SERVER's copy, not the winner's.
   */
  private async reconcile(
    input: SaveDashboardInput,
    scope: DashboardScope,
  ): Promise<DashboardDefinitionDto | null> {
    if (input.id == null) return null
    const store = useDashboardStore()
    try {
      // Refetch only the owning workspace/account and patch that ONE record. A full
      // `loadDefinitions` fan-out would re-request every workspace the user can see just to
      // resolve one stale row — and would go through the store's transport rather than this
      // flow's injected one, which is a second seam nobody would remember to wire in a test.
      const isAccountCenter = scope === 'accountCenter'
      const list = isAccountCenter
        ? await this.api.listAccountCenters(input.ownerId)
        : ((await this.api.listDefinitions(input.ownerId)).data ?? [])
      const latest = list.find((entry) => Number(entry.id) === Number(input.id)) ?? null

      if (latest) {
        if (isAccountCenter) store.upsertAccountCenterLocal(latest)
        else store.upsertLocal(latest)
      }
      return latest
    } catch {
      // The reload failing does not change the verdict: the save still lost the race, and the
      // caller still must not re-send. `null` says "reapply, but we could not fetch theirs".
      return null
    }
  }
}
